import {
  FilterResultMethod,
  FilterResults,
  FilterResultsKeyData,
  screener,
} from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useWindowSize } from '@terminal/hooks/UseWindowSize';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { WatchlistEntities } from '@terminal/stores/use-watchlist/watchlist-types';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import {
  EntityPlusPrice,
  useAssetsTableData,
} from '@terminal/views/dashboard/my-assets-widget/assets-table/useAssetsTableData';
import {
  ColumnMetaWithKey,
  getPinnedColumnsMeta,
} from '@terminal/views/screener/results/screener-table-utils';
import { hasEmptyEntityTagValues } from '@terminal/widgets/scenario/hooks/use-screener-entities-tags/entity-screener-service/utils';
import { t } from 'i18next';
import { useMemo, useState } from 'react';

export const LIVE_RETURN_1D_FIELD = 'liveReturn1D';
export const PREV_RETURN_1D_FIELD = 'return1D';
export const DEFAULT_TAB = 'overview';
const QUERY_KEY_WATCHLIST_TABLE = 'QUERY_KEY_WATCHLIST_TABLE';

const MIN_PAGE_SIZE = 10;
// Rough upper estimate of the row height
const ESTIMATED_ROW_HEIGHT = 50;

export const getPageSizeFromViewportHeight = (
  windowHeight: number,
  isWatchlistPage: boolean
) =>
  isWatchlistPage
    ? Math.max(MIN_PAGE_SIZE, Math.floor(windowHeight / ESTIMATED_ROW_HEIGHT))
    : MIN_PAGE_SIZE;

const getOverviewColumnsMeta = (isWatchlistPage: boolean) => {
  const defaultFields = {
    value_type: 'float',
    directional: true,
  };

  const perfColumns = [
    {
      field: 'return1W',
      key: DEFAULT_TAB,
      name: t('widget:watchlist.oneWeekChange'),
      ...defaultFields,
    },
    {
      field: 'return1M',
      key: DEFAULT_TAB,
      name: t('widget:watchlist.oneMonthChange'),
      ...defaultFields,
    },
    {
      field: 'return3M',
      key: DEFAULT_TAB,
      name: t('widget:watchlist.threeMonthsChange'),
      ...defaultFields,
    },
  ];

  return [
    {
      field: LIVE_RETURN_1D_FIELD,
      key: DEFAULT_TAB,
      name: t('widget:watchlist.oneDayChange'),
      ...defaultFields,
    },
    ...(isWatchlistPage
      ? [
          {
            field: PREV_RETURN_1D_FIELD,
            key: DEFAULT_TAB,
            name: t('widget:watchlist.prevOneDayChange'),
            ...defaultFields,
          },
        ]
      : []),
    ...(isWatchlistPage ? perfColumns : []),
  ] as ColumnMetaWithKey[];
};

export interface UseWatchlistTableProps {
  entities: WatchlistEntities[];
  tab?: string;
  isWatchlistPage?: boolean;
}

export const useWatchlistTable = ({
  entities,
  tab = DEFAULT_TAB,
  isWatchlistPage = false,
}: UseWatchlistTableProps) => {
  const { windowHeight } = useWindowSize();
  const [pinnedColumns, setPinnedColumns] = useState<Record<string, string[]>>(
    {}
  );
  const isApiScreenerEntitiesV3FlagEnabled = useFlags(state =>
    state.getFeatureFlagState('DATA-1243-api_screener_entities_v3')
  );

  const pageSize = useMemo(
    () => getPageSizeFromViewportHeight(windowHeight, isWatchlistPage),
    [windowHeight, isWatchlistPage]
  );

  const {
    data: assetsQueryResult,
    fetchNextPage: fetchOverviewData,
    hasNextPage: hasOverviewNextPage,
  } = useAssetsTableData(entities, pageSize);

  const overviewTabData = useMemo(
    () => assetsQueryResult?.pages?.flatMap(page => page) ?? [],
    [assetsQueryResult]
  );

  //Disabled on the dashboard page
  const filterResultQueryResult = useInfiniteQuery<
    FilterResults,
    Error,
    InfiniteData<FilterResults>,
    QueryKey,
    number
  >({
    queryKey: [
      QUERY_KEY_WATCHLIST_TABLE,
      entities,
      pageSize,
      isApiScreenerEntitiesV3FlagEnabled,
    ],
    queryFn: async ({ signal, pageParam }) => {
      const postFilterPath = isApiScreenerEntitiesV3FlagEnabled
        ? screener.postFilterv3.path
        : screener.postFilter.path;
      const start = pageParam * pageSize;
      const tags = entities.slice(start, start + pageSize).map(e => e.tag);

      const requestFilters = {
        entity_tag: {
          children: [],
          values: [{ tags }],
        },
      };

      if (hasEmptyEntityTagValues(requestFilters)) {
        return {
          entities: [],
          data: [],
          page: {
            current: pageParam,
            size: pageSize,
            next: undefined,
          },
          total_count: 0,
        };
      }

      return wretchRequest<FilterResults>(postFilterPath, {
        method: 'post',
        body: {
          filters: requestFilters,
        },
        signal,
      });
    },
    staleTime: getTimeTillEndOfDayMs(),
    enabled: isWatchlistPage && !!entities.length,
    initialPageParam: 0,
    getNextPageParam: (_, allPages, lastPageParam) => {
      const allEntities = allPages.flatMap(p => p.entities);
      return allEntities.length === entities.length ? null : lastPageParam + 1;
    },
  });

  const isOverviewTab = tab === DEFAULT_TAB;
  const filterResults = useMemo(
    () => filterResultQueryResult.data?.pages?.flatMap(page => page) ?? [],
    [filterResultQueryResult.data]
  );
  const filterResultsEntities = useMemo(
    () => filterResults.flatMap(e => e.entities),
    [filterResults]
  );
  const filterResultsData = useMemo(
    () => filterResults.flatMap(e => e.data),
    [filterResults]
  );

  const tabDataIdx = filterResults[0]?.data.findIndex(c => c.key === tab);

  const categoryData = filterResults[0] && {
    ...filterResults[0].data[tabDataIdx],
    data: filterResults.flatMap(item => item.data[tabDataIdx]?.data),
  };

  const pinnedColumnsMeta =
    filterResults[0] &&
    getPinnedColumnsMeta({
      filterResults: filterResults[0],
      pinnedColumns,
      categoryData,
    });

  const columnsMeta = useMemo(
    () =>
      isOverviewTab
        ? getOverviewColumnsMeta(isWatchlistPage)
        : (categoryData?.meta.map(c => ({
            ...c,
            key: categoryData.key,
          })) ?? []),
    [categoryData?.meta, tab, isWatchlistPage, t]
  );

  const mapTagToDataIndex = useMemo(
    () =>
      filterResultsEntities.reduce<Record<string, number>>(
        (res, entity, idx) => {
          res[entity] = idx;
          return res;
        },
        {}
      ),
    [filterResultsEntities]
  );

  const watchlistTableData = useMemo(() => {
    if (isOverviewTab) {
      // dashboard page
      if (!filterResults.length || !pinnedColumnsMeta?.length) {
        return overviewTabData;
      }

      const pinnedCategoriesData = pinnedColumnsMeta.map(p => {
        const categoryData = filterResultsData.find(
          item => item.key === p.key
        ) as FilterResultsKeyData;
        return { categoryData, field: p.field };
      });

      return overviewTabData.map(tableRow => {
        const idx = mapTagToDataIndex[tableRow.tag];

        if (idx === undefined) {
          return tableRow;
        }

        const pinnedData = pinnedCategoriesData.reduce(
          (res, item) => {
            const row = item.categoryData?.data?.[idx];

            if (row) {
              res[item.field] = row[item.field];
            }

            return res;
          },
          {} as Record<string, FilterResultMethod>
        );
        return {
          ...tableRow,
          ...pinnedData,
        } as EntityPlusPrice;
      });
    }

    if (categoryData?.data) {
      return overviewTabData.reduce((res, item) => {
        const index = mapTagToDataIndex[item.tag];

        if (index > -1) {
          const tableRow = {
            asset: item.asset,
            tag: item.tag,
            ...categoryData.data[index],
          } as EntityPlusPrice;

          res.push(tableRow);
        }

        return res;
      }, [] as EntityPlusPrice[]);
    }

    return [];
  }, [
    overviewTabData,
    filterResults,
    pinnedColumns,
    isOverviewTab,
    categoryData,
  ]);

  const onPinChange = (columnMeta: ColumnMetaWithKey, pin: boolean) => {
    setPinnedColumns(prev => {
      const key = columnMeta.key;
      const fields = prev[key] ?? [];
      return {
        ...prev,
        [key]: pin
          ? [...fields, columnMeta.field]
          : fields.filter(f => columnMeta.field !== f),
      };
    });
  };

  return {
    filterResultQueryResult,
    fetchOverviewData,
    hasOverviewNextPage,
    columnsMeta,
    watchlistTableData,
    categories:
      filterResults[0]?.data.map(c => ({ key: c.key, label: c.category })) ??
      [],
    onPinChange,
    pinnedFields: Object.values(pinnedColumns).flat(),
    pinnedColumnsMeta,
  };
};
